import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  TouchableOpacity,
  LayoutAnimation,
  Platform,
  UIManager,
  TextInput,
  Keyboard,
  Animated,
} from "react-native";
import AnimatedRN, { FadeInDown } from "react-native-reanimated";
import { Text } from "@louise/ui/Text/Text";
import {
  Clock,
  Heart,
  Activity,
  ScrollText,
  Stethoscope,
  Sparkles,
  ArrowRight,
  History,
  ChevronRight,
  Check,
  X,
  Pencil,
} from "lucide-react-native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import { useTranslations } from "@src/packages/i18n";
import type {
  UpdatePatientPayload,
  DiseaseType,
  Patient,
} from "@src/packages/patients/patients.types";
import type {
  PreservationReason,
  PeriodRegularity,
  PeriodPainSeverity,
} from "../fertilityIntent.types";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
if (
  Platform.OS === "android" &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

// Step 1: Preservation Reasons
interface ReasonOption {
  id: PreservationReason;
  label: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
}

const REASON_OPTIONS: ReasonOption[] = [
  {
    id: "more_time",
    label: "I want more time before trying for a baby",
    icon: Clock,
  },
  {
    id: "no_partner",
    label: "I don't have the right partner yet / I'm single",
    icon: Heart,
  },
  {
    id: "medical_reason",
    label: "A medical reason or upcoming treatment",
    icon: Activity,
  },
  {
    id: "family_history",
    label: "Family history of infertility or early menopause",
    icon: ScrollText,
  },
  {
    id: "doctor_advised",
    label: "I was advised to do it by a doctor",
    icon: Stethoscope,
  },
  {
    id: "other",
    label: "Other",
    icon: Sparkles,
  },
];

// Step 2: Medical Conditions — IDs match disease enum values
const MEDICAL_CONDITION_OPTIONS: { id: string; label: string }[] = [
  { id: "endometriosis", label: "Endometriosis" },
  { id: "pcos", label: "PMOS (ex PCOS)" },
  {
    id: "diminished_ovarian_reserve",
    label: "Low ovarian reserve / diminished ovarian reserve",
  },
  {
    id: "primary_ovarian_insufficiency",
    label: "Early menopause / premature ovarian insufficiency",
  },
  { id: "thyroid_disease", label: "Thyroid disease" },
  { id: "fibroids", label: "Fibroids or adenomyosis" },
  { id: "tubal_issues", label: "Tubal issues or pelvic inflammatory disease" },
  { id: "ovarian_surgery", label: "Previous ovarian surgery" },
  { id: "none_or_unsure", label: "None of these / I'm not sure" },
];

// "Previous ovarian surgery" is stored as a generic `surgery: "other"` row
// tagged with this fixed label; it's the marker used to find/remove that row.
const OVARIAN_SURGERY_LABEL = "Ovarian surgery";

// Reconstruct the selected-condition ids this card manages from saved patient
// data: diseases, the marker ovarian-surgery row, and the tubal infertility
// cause. Used to seed/restore selection so the save-time diff treats an absent
// selection as a real deselection rather than spuriously deleting saved rows.
function deriveManagedConditions(patient: Patient | undefined): string[] {
  const conditions: string[] = (patient?.medicalHistory?.diseases ?? [])
    .map((d) => d.disease)
    .filter((d): d is NonNullable<typeof d> => !!d);
  const hasOvarianSurgery = (patient?.medicalHistory?.surgeries ?? []).some(
    (s) => s.surgery === "other" && s.other === OVARIAN_SURGERY_LABEL,
  );
  if (hasOvarianSurgery) {
    conditions.push("ovarian_surgery");
  }
  const hasTubalCause = (
    patient?.fertilityHistory?.infertilityHistory?.causes ?? []
  ).some((c) => c.cause === "tubal_causes");
  if (hasTubalCause) {
    conditions.push("tubal_issues");
  }
  return conditions;
}

// Step 3: Period Description
const PERIOD_DESCRIPTION_OPTIONS: { id: PeriodRegularity; label: string }[] = [
  { id: "regular", label: "I have regular periods (roughly every 25–35 days)" },
  { id: "irregular", label: "My periods are very irregular or unpredictable" },
  { id: "no_periods", label: "I don't get periods / they have stopped" },
  { id: "unsure", label: "I'm not sure" },
];

// Step 4: Period Pain (only shown if they have periods)
const PERIOD_PAIN_OPTIONS: { id: PeriodPainSeverity; label: string }[] = [
  { id: "mild", label: "Mild or manageable" },
  {
    id: "severe",
    label: "Very strong (missing work/school or needing strong painkillers)",
  },
];

export type FertilityIntentCardProps = {
  onNavigateToMedicalHistory?: () => void;
};

type CardState = "in_progress" | "completed" | "editing";

const TOTAL_STEPS = 4;

export function FertilityIntentCard({
  onNavigateToMedicalHistory,
}: FertilityIntentCardProps = {}) {
  const t = useTranslations();
  const capturePress = usePressCapture("FertilityIntentCard");
  const { data: patient, isLoading } = usePatient();
  const updatePatient = useQueuedPatientUpdate();
  const fertilityIntent = patient?.fertilityIntent;
  const isSaving = updatePatient.isPending;
  const [saveFailed, setSaveFailed] = useState(false);

  // Safety check - ensure we're not in an error state
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to ensure component is fully mounted
    const timer = setTimeout(() => setIsReady(true), 0);
    return () => clearTimeout(timer);
  }, []);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [cardState, setCardState] = useState<CardState>("in_progress");

  // Step 1: Preservation reasons
  const [selectedReasons, setSelectedReasons] = useState<PreservationReason[]>(
    [],
  );
  const [otherReasonText, setOtherReasonText] = useState<string>("");
  const [showOtherSheet, setShowOtherSheet] = useState(false);
  const slideAnim = useRef(new Animated.Value(0)).current;

  // Step 2: Medical conditions
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);

  // Step 3: Period description
  const [periodDescription, setPeriodDescription] =
    useState<PeriodRegularity | null>(null);

  // Step 4: Period pain (only relevant if they have periods)
  const [periodPain, setPeriodPain] = useState<PeriodPainSeverity | null>(null);

  // Track if we've initialized from fertility intent data
  const hasInitialized = useRef(false);

  // Sync with fertility intent data when it loads (only on initial load, not when editing)
  useEffect(() => {
    if (!hasInitialized.current && cardState !== "editing" && fertilityIntent) {
      // Restore all saved data
      if (fertilityIntent.preservationReasons?.length > 0) {
        setSelectedReasons(fertilityIntent.preservationReasons);
      }
      if (fertilityIntent.preservationReasonsOther) {
        setOtherReasonText(fertilityIntent.preservationReasonsOther);
      }
      // Restore every condition this card manages (diseases + ovarian surgery
      // + tubal cause) so the save-time diff doesn't read an absent selection
      // as a deletion.
      const restored = deriveManagedConditions(patient);
      if (restored.length > 0) {
        setSelectedConditions(restored);
      }
      if (fertilityIntent.periodRegularity) {
        setPeriodDescription(fertilityIntent.periodRegularity);
      }
      if (fertilityIntent.periodPainSeverity) {
        setPeriodPain(fertilityIntent.periodPainSeverity);
      }
      // Mark as completed if we have completedAt timestamp
      if (fertilityIntent.completedAt) {
        setCardState("completed");
      }
      hasInitialized.current = true;
    }
  }, [fertilityIntent, cardState, patient]);

  // Determine if step 4 should be shown (only if they have periods)
  const shouldShowStep4 =
    periodDescription === "regular" || periodDescription === "irregular";
  const effectiveTotalSteps = shouldShowStep4 ? TOTAL_STEPS : TOTAL_STEPS - 1;

  // Close bottom sheet
  const closeOtherSheet = React.useCallback(() => {
    Animated.spring(slideAnim, {
      toValue: 0,
      useNativeDriver: true,
      tension: 50,
      friction: 8,
    }).start(() => {
      setShowOtherSheet(false);
    });
    Keyboard.dismiss();
  }, [slideAnim]);

  // Step 1: Toggle preservation reason
  const toggleReason = React.useCallback(
    (reasonId: PreservationReason) => {
      try {
        if (Platform.OS !== "web") {
          LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        }
      } catch {
        // Ignore LayoutAnimation errors
      }

      if (reasonId === "other") {
        // If selecting "other", open the bottom sheet
        if (!selectedReasons.includes("other")) {
          setSelectedReasons((prev) => [...prev, "other"]);
          setShowOtherSheet(true);
          // Animate slide up
          Animated.spring(slideAnim, {
            toValue: 1,
            useNativeDriver: true,
            tension: 50,
            friction: 8,
          }).start();
        } else {
          // If deselecting "other", close sheet and clear text
          setSelectedReasons((prev) => prev.filter((r) => r !== "other"));
          setOtherReasonText("");
          closeOtherSheet();
        }
      } else {
        // For other reasons, just toggle
        setSelectedReasons((prev) => {
          const newReasons = prev.includes(reasonId)
            ? prev.filter((r) => r !== reasonId)
            : [...prev, reasonId];

          return newReasons;
        });
      }
    },
    [selectedReasons, closeOtherSheet, slideAnim],
  );

  // Save other reason text
  const saveOtherReason = useCallback(() => {
    // The text is already in state, just close the sheet
    closeOtherSheet();
  }, [closeOtherSheet]);

  // Don't auto-save - only save when user clicks Continue or completes all steps
  // This avoids navigation context issues during rapid state updates

  // Step 2: Toggle medical condition
  const toggleCondition = (conditionId: string) => {
    try {
      if (Platform.OS !== "web") {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
    } catch {
      // Ignore LayoutAnimation errors
    }

    setSelectedConditions((prev) => {
      // If "none_or_unsure" is selected, clear all others
      if (conditionId === "none_or_unsure") {
        return prev.includes("none_or_unsure") ? [] : ["none_or_unsure"];
      }
      // If selecting something else, remove "none_or_unsure"
      const filtered = prev.filter((c) => c !== "none_or_unsure");
      return filtered.includes(conditionId)
        ? filtered.filter((c) => c !== conditionId)
        : [...filtered, conditionId];
    });
  };

  // Step 3: Select period description
  const selectPeriodDescription = (description: PeriodRegularity) => {
    try {
      if (Platform.OS !== "web") {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
    } catch {
      // Ignore LayoutAnimation errors
    }

    setPeriodDescription(description);
    // If they don't have periods, clear period pain
    if (description === "no_periods" || description === "unsure") {
      setPeriodPain(null);
    }
  };

  // Step 4: Select period pain
  const selectPeriodPain = (pain: PeriodPainSeverity) => {
    try {
      if (Platform.OS !== "web") {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
    } catch {
      // Ignore LayoutAnimation errors
    }

    setPeriodPain(pain);
  };

  // Navigate to next step
  const handleContinue = () => {
    if (currentStep < effectiveTotalSteps) {
      try {
        if (Platform.OS !== "web") {
          LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        }
      } catch {
        // Ignore LayoutAnimation errors
      }

      const nextStep = currentStep + 1;

      // Skip step 4 if they don't have periods (after step 3)
      if (currentStep === 3 && !shouldShowStep4) {
        // Step 4 is skipped, so we're done
        void saveAllData();
        return;
      }

      setCurrentStep(nextStep);
    } else {
      // All steps complete - save all data
      void saveAllData();
    }
  };

  // Save all collected data
  const saveAllData = async () => {
    if (!periodDescription) {
      console.warn("Period description is required");
      return;
    }

    // The patient sub-collections are incremental upserts, not full
    // replacements: to remove a row we send { id, op: "delete" }, and to avoid
    // duplicating an existing row we simply don't re-send it. So diff the
    // current selection against what's already saved.
    type MedicalHistoryPayload = NonNullable<
      UpdatePatientPayload["medicalHistory"]
    >;
    type DiseasePayload = NonNullable<
      MedicalHistoryPayload["diseases"]
    >[number];
    type SurgeryPayload = NonNullable<
      MedicalHistoryPayload["surgeries"]
    >[number];
    type InfertilityHistoryPayload = NonNullable<
      NonNullable<
        UpdatePatientPayload["fertilityHistory"]
      >["infertilityHistory"]
    >;
    type CausePayload = NonNullable<
      InfertilityHistoryPayload["causes"]
    >[number];

    const ENDOCRINE = new Set(["pcos", "thyroid_disease"]);
    const DISEASE_IDS = new Set<string>([
      "pcos",
      "endometriosis",
      "thyroid_disease",
      "diminished_ovarian_reserve",
      "primary_ovarian_insufficiency",
      "fibroids",
    ]);

    const mhId = patient?.medicalHistory?.id;
    const existingDiseases = patient?.medicalHistory?.diseases ?? [];
    const existingSurgeries = patient?.medicalHistory?.surgeries ?? [];
    const existingInfertility =
      patient?.fertilityHistory?.infertilityHistory ?? null;
    const existingCauses = existingInfertility?.causes ?? [];

    const selectedDiseases = new Set(
      selectedConditions.filter((c) => DISEASE_IDS.has(c)),
    );

    // Diseases: add newly-selected, delete previously-saved-but-now-deselected,
    // leave unchanged ones alone (re-sending without an id would duplicate).
    const diseasesPayload: DiseasePayload[] = [];
    selectedDiseases.forEach((c) => {
      const alreadySaved = existingDiseases.some((d) => d.disease === c);
      if (!alreadySaved) {
        diseasesPayload.push({
          disease: c as DiseaseType,
          category: ENDOCRINE.has(c) ? "endocrine" : "other",
        });
      }
    });
    existingDiseases.forEach((d) => {
      if (
        d.id &&
        d.disease &&
        DISEASE_IDS.has(d.disease) &&
        !selectedDiseases.has(d.disease)
      ) {
        diseasesPayload.push({ id: d.id, op: "delete" });
      }
    });

    // "Previous ovarian surgery" — a single marker surgery row.
    const wantOvarianSurgery = selectedConditions.includes("ovarian_surgery");
    const existingOvarianSurgery = existingSurgeries.find(
      (s) => s.surgery === "other" && s.other === OVARIAN_SURGERY_LABEL,
    );
    const surgeriesPayload: SurgeryPayload[] = [];
    if (wantOvarianSurgery && !existingOvarianSurgery) {
      surgeriesPayload.push({ surgery: "other", other: OVARIAN_SURGERY_LABEL });
    } else if (!wantOvarianSurgery && existingOvarianSurgery?.id) {
      surgeriesPayload.push({
        id: existingOvarianSurgery.id,
        surgery: "other",
        op: "delete",
      });
    }

    const payload: UpdatePatientPayload = {
      fertilityIntent: {
        preservationReasons: selectedReasons,
        preservationReasonsOther: selectedReasons.includes("other")
          ? otherReasonText
          : null,
        periodRegularity: periodDescription,
        periodPainSeverity: shouldShowStep4 ? periodPain : null,
        completedAt: new Date().toISOString(),
      },
    };
    if (diseasesPayload.length > 0 || surgeriesPayload.length > 0) {
      payload.medicalHistory = {
        ...(mhId ? { id: mhId } : {}),
        ...(diseasesPayload.length > 0 ? { diseases: diseasesPayload } : {}),
        ...(surgeriesPayload.length > 0 ? { surgeries: surgeriesPayload } : {}),
      };
    }

    // Tubal issues — a single infertility cause. Delete ops are only valid when
    // an infertilityHistory already exists; otherwise the backend treats every
    // entry as a create, so we omit deletes in that case.
    const wantTubal = selectedConditions.includes("tubal_issues");
    const existingTubal = existingCauses.find(
      (c) => c.cause === "tubal_causes",
    );
    const infertilityHistoryId =
      existingInfertility?.id ?? existingCauses[0]?.infertilityHistoryId;
    const causesPayload: CausePayload[] = [];
    if (wantTubal && !existingTubal) {
      causesPayload.push({ cause: "tubal_causes" });
    } else if (!wantTubal && existingTubal?.id && infertilityHistoryId) {
      causesPayload.push({ id: existingTubal.id, op: "delete" });
    }
    if (causesPayload.length > 0) {
      payload.fertilityHistory = {
        infertilityHistory: {
          ...(infertilityHistoryId ? { id: infertilityHistoryId } : {}),
          causes: causesPayload,
        },
      };
    }

    setSaveFailed(false);
    try {
      await updatePatient.mutateAsync(payload);
      setCardState("completed");
    } catch {
      setSaveFailed(true);
    }
  };

  // Handle review/edit - reset to step 1
  const handleReviewEdit = () => {
    setCurrentStep(1);
    setCardState("editing");
  };

  // Handle cancel edit - restore original data and return to completed state
  const handleCancelEdit = () => {
    if (fertilityIntent) {
      setSelectedReasons(fertilityIntent.preservationReasons ?? []);
      setOtherReasonText(fertilityIntent.preservationReasonsOther ?? "");
      setSelectedConditions(deriveManagedConditions(patient));
      setPeriodDescription(fertilityIntent.periodRegularity ?? null);
      setPeriodPain(fertilityIntent.periodPainSeverity ?? null);
    }
    setCurrentStep(1);
    setCardState("completed");
  };

  const handleAccessMedicalHistory = () => {
    if (onNavigateToMedicalHistory) {
      onNavigateToMedicalHistory();
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <View className="mb-4">
              <Text className="text-[0.95rem] font-medium leading-snug text-slate-100">
                Why are you thinking about preservation?
              </Text>
              <Text className="mt-1 text-[0.75rem] text-slate-400">
                (You can choose more than one)
              </Text>
            </View>
            <View style={{ gap: 12 }}>
              {REASON_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = selectedReasons.includes(option.id);
                const handlePress = () => {
                  try {
                    toggleReason(option.id);
                  } catch (error) {
                    console.error("Error toggling reason:", error);
                  }
                };

                return (
                  <TouchableOpacity
                    key={option.id}
                    onPress={() => {
                      capturePress(pressLabel(option.label), "intent_option");
                      handlePress();
                    }}
                    activeOpacity={0.7}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      borderRadius: 16,
                      borderWidth: 1,
                      padding: 12,
                      borderColor: isSelected
                        ? "#4ADE80"
                        : "rgba(255, 255, 255, 0.1)",
                      backgroundColor: isSelected
                        ? "rgba(74, 222, 128, 0.05)"
                        : "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <View
                      style={{
                        height: 32,
                        width: 32,
                        flexShrink: 0,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? "rgba(74, 222, 128, 0.3)"
                          : "rgba(255, 255, 255, 0.1)",
                        backgroundColor: isSelected
                          ? "rgba(74, 222, 128, 0.1)"
                          : "rgba(255, 255, 255, 0.05)",
                      }}
                    >
                      <Icon
                        size={16}
                        color={isSelected ? "#4ADE80" : "#64748b"}
                      />
                    </View>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 13.6,
                        lineHeight: 18,
                        color: isSelected ? "#ffffff" : "#cbd5e1",
                        fontWeight: isSelected ? "600" : "400",
                      }}
                    >
                      {option.label}
                    </Text>
                    {isSelected && (
                      <View
                        style={{
                          flexShrink: 0,
                          height: 16,
                          width: 16,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Check size={16} color="#4ADE80" strokeWidth={2.5} />
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        );

      case 2:
        return (
          <>
            <View className="mb-4">
              <Text className="text-[0.95rem] font-medium leading-snug text-slate-100">
                Have you ever been told you have any of the following?
              </Text>
              <Text className="mt-1 text-[0.75rem] text-slate-400">
                (You can choose more than one, or skip)
              </Text>
            </View>
            <View style={{ gap: 12 }}>
              {MEDICAL_CONDITION_OPTIONS.map((option) => {
                const isSelected = selectedConditions.includes(option.id);
                return (
                  <TouchableOpacity
                    key={option.id}
                    onPress={() => {
                      capturePress(
                        pressLabel(option.label),
                        "condition_option",
                      );
                      toggleCondition(option.id);
                    }}
                    activeOpacity={0.7}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      borderRadius: 16,
                      borderWidth: 1,
                      padding: 12,
                      borderColor: isSelected
                        ? "#4ADE80"
                        : "rgba(255, 255, 255, 0.1)",
                      backgroundColor: isSelected
                        ? "rgba(74, 222, 128, 0.05)"
                        : "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <View
                      style={{
                        height: 32,
                        width: 32,
                        flexShrink: 0,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? "rgba(74, 222, 128, 0.3)"
                          : "rgba(255, 255, 255, 0.1)",
                        backgroundColor: isSelected
                          ? "rgba(74, 222, 128, 0.1)"
                          : "rgba(255, 255, 255, 0.05)",
                      }}
                    >
                      {isSelected ? (
                        <Check size={16} color="#4ADE80" strokeWidth={2.5} />
                      ) : (
                        <View
                          style={{
                            height: 12,
                            width: 12,
                            borderRadius: 4,
                            borderWidth: 1,
                            borderColor: "#94a3b8",
                          }}
                        />
                      )}
                    </View>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 13.6,
                        lineHeight: 18,
                        color: isSelected ? "#ffffff" : "#cbd5e1",
                        fontWeight: isSelected ? "600" : "400",
                      }}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        );

      case 3:
        return (
          <>
            <View style={{ marginBottom: 16 }}>
              <Text
                style={{
                  fontSize: 15.2,
                  fontWeight: "500",
                  lineHeight: 20,
                  color: "#f1f5f9",
                }}
              >
                How would you describe your periods?
              </Text>
            </View>
            <View style={{ gap: 12 }}>
              {PERIOD_DESCRIPTION_OPTIONS.map((option) => {
                const isSelected = periodDescription === option.id;
                return (
                  <TouchableOpacity
                    key={option.id}
                    onPress={() => {
                      capturePress(
                        pressLabel(option.label),
                        "period_description_option",
                      );
                      selectPeriodDescription(option.id);
                    }}
                    activeOpacity={0.7}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      borderRadius: 16,
                      borderWidth: 1,
                      padding: 12,
                      borderColor: isSelected
                        ? "#4ADE80"
                        : "rgba(255, 255, 255, 0.1)",
                      backgroundColor: isSelected
                        ? "rgba(74, 222, 128, 0.05)"
                        : "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <View
                      style={{
                        height: 20,
                        width: 20,
                        flexShrink: 0,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? "#4ADE80"
                          : "rgba(255, 255, 255, 0.2)",
                        backgroundColor: isSelected
                          ? "rgba(74, 222, 128, 0.1)"
                          : "transparent",
                      }}
                    >
                      {isSelected && (
                        <View
                          style={{
                            height: 10,
                            width: 10,
                            borderRadius: 5,
                            backgroundColor: "#4ADE80",
                          }}
                        />
                      )}
                    </View>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 13.6,
                        lineHeight: 18,
                        color: isSelected ? "#ffffff" : "#cbd5e1",
                        fontWeight: isSelected ? "600" : "400",
                      }}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        );

      case 4:
        if (!shouldShowStep4) {
          // Shouldn't reach here, but handle gracefully
          return null;
        }
        return (
          <>
            <View style={{ marginBottom: 16 }}>
              <Text
                style={{
                  fontSize: 15.2,
                  fontWeight: "500",
                  lineHeight: 20,
                  color: "#f1f5f9",
                }}
              >
                And your period pain?
              </Text>
            </View>
            <View style={{ gap: 12 }}>
              {PERIOD_PAIN_OPTIONS.map((option) => {
                const isSelected = periodPain === option.id;
                return (
                  <TouchableOpacity
                    key={option.id}
                    onPress={() => {
                      capturePress(
                        pressLabel(option.label),
                        "period_pain_option",
                      );
                      selectPeriodPain(option.id);
                    }}
                    activeOpacity={0.7}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      borderRadius: 16,
                      borderWidth: 1,
                      padding: 12,
                      borderColor: isSelected
                        ? "#4ADE80"
                        : "rgba(255, 255, 255, 0.1)",
                      backgroundColor: isSelected
                        ? "rgba(74, 222, 128, 0.05)"
                        : "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <View
                      style={{
                        height: 20,
                        width: 20,
                        flexShrink: 0,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? "#4ADE80"
                          : "rgba(255, 255, 255, 0.2)",
                        backgroundColor: isSelected
                          ? "rgba(74, 222, 128, 0.1)"
                          : "transparent",
                      }}
                    >
                      {isSelected && (
                        <View
                          style={{
                            height: 10,
                            width: 10,
                            borderRadius: 5,
                            backgroundColor: "#4ADE80",
                          }}
                        />
                      )}
                    </View>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 13.6,
                        lineHeight: 18,
                        color: isSelected ? "#ffffff" : "#cbd5e1",
                        fontWeight: isSelected ? "600" : "400",
                      }}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        );

      default:
        return null;
    }
  };

  // Determine if continue button should be enabled
  const canContinue = () => {
    switch (currentStep) {
      case 1:
        return true; // Can always continue from step 1 (optional)
      case 2:
        return true; // Can skip step 2
      case 3:
        return periodDescription !== null;
      case 4:
        return periodPain !== null;
      default:
        return false;
    }
  };

  const getContinueButtonText = () => {
    if (currentStep === effectiveTotalSteps) {
      return "Complete";
    }
    return "Continue";
  };

  // Generate summary of user answers for completion state
  const getSummaryItems = () => {
    const items: string[] = [];

    // Preservation reasons summary
    if (selectedReasons.length > 0) {
      const reasonLabels = selectedReasons
        .filter((r) => r !== "other")
        .map((r) => REASON_OPTIONS.find((o) => o.id === r)?.label)
        .filter(Boolean);
      if (selectedReasons.includes("other") && otherReasonText) {
        reasonLabels.push(otherReasonText);
      }
      if (reasonLabels.length > 0) {
        items.push(
          reasonLabels.length === 1
            ? (reasonLabels[0] as string)
            : `${reasonLabels.length} reasons selected`,
        );
      }
    }

    // Conditions summary
    if (selectedConditions.length > 0) {
      if (selectedConditions.includes("none_or_unsure")) {
        items.push("No known conditions");
      } else {
        const conditionLabels = selectedConditions
          .map((c) => MEDICAL_CONDITION_OPTIONS.find((o) => o.id === c)?.label)
          .filter(Boolean);
        items.push(
          conditionLabels.length === 1
            ? (conditionLabels[0] as string)
            : `${conditionLabels.length} conditions noted`,
        );
      }
    }

    // Period summary
    if (periodDescription) {
      const periodLabel = PERIOD_DESCRIPTION_OPTIONS.find(
        (o) => o.id === periodDescription,
      )?.label;
      if (periodLabel) {
        // Shorten the label
        const shortPeriod =
          periodDescription === "regular"
            ? "Regular periods"
            : periodDescription === "irregular"
              ? "Irregular periods"
              : periodDescription === "no_periods"
                ? "No periods"
                : "Period status unsure";
        items.push(shortPeriod);
      }
    }

    // Pain summary (only if applicable)
    if (shouldShowStep4 && periodPain) {
      items.push(
        periodPain === "mild" ? "Mild period pain" : "Severe period pain",
      );
    }

    return items;
  };

  if (!isReady || isLoading) {
    return null; // Don't render until ready
  }

  // Show completion state
  if (cardState === "completed") {
    return (
      <AnimatedRN.View
        entering={FadeInDown.delay(400)}
        style={{ marginBottom: 32 }}
      >
        <Text className="mb-3 text-[0.95rem] font-medium tracking-normal text-white">
          Let&apos;s Tailor Your Experience
        </Text>

        <View className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 p-5 backdrop-blur-xl">
          {/* Summary of answers */}
          <View style={{ gap: 8 }}>
            {getSummaryItems().map((item, index) => (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Check size={14} color="#4ADE80" strokeWidth={2.5} />
                <Text
                  style={{
                    fontSize: 13.6,
                    color: "#cbd5e1",
                    flex: 1,
                  }}
                >
                  {item}
                </Text>
              </View>
            ))}
          </View>

          {/* Review/edit link */}
          <TouchableOpacity
            onPress={() => {
              capturePress(pressLabel("Edit answers"), "edit_intent_answers");
              handleReviewEdit();
            }}
            activeOpacity={0.7}
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTopWidth: 1,
              borderTopColor: "rgba(255, 255, 255, 0.05)",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <Pencil size={12} color="#64748b" />
            <Text
              style={{
                fontSize: 12,
                fontWeight: "500",
                color: "#64748b",
              }}
            >
              Edit answers
            </Text>
          </TouchableOpacity>
        </View>
      </AnimatedRN.View>
    );
  }

  return (
    <>
      <AnimatedRN.View
        entering={FadeInDown.delay(400)}
        style={{ marginBottom: 32 }}
      >
        <Text className="mb-3 text-[0.95rem] font-medium tracking-normal text-white">
          Let&apos;s tailor your experience
        </Text>

        <View className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 p-6 backdrop-blur-xl">
          {/* Step indicator */}
          <Text className="my-2 truncate text-[0.75rem] text-slate-400">
            STEP {currentStep}
          </Text>

          {/* Progress bars */}
          <View className="mb-6 flex-row gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber < currentStep;
              const isCurrent = stepNumber === currentStep;
              // Skip step 4 in progress bar if not needed
              if (stepNumber === 4 && !shouldShowStep4 && currentStep < 4) {
                return null;
              }
              return (
                <View
                  key={index}
                  className={`h-1 flex-1 rounded-sm ${
                    isActive || isCurrent
                      ? "bg-gradient-to-r from-[#86EFAC] to-[#C4B5FD]"
                      : "bg-white/10"
                  }`}
                  style={
                    isActive || isCurrent
                      ? { backgroundColor: "#86EFAC" }
                      : { backgroundColor: "rgba(255, 255, 255, 0.1)" }
                  }
                />
              );
            })}
          </View>

          {/* Step content */}
          {renderStepContent()}

          {saveFailed ? (
            <Text
              accessibilityLiveRegion="polite"
              className="mt-4 text-[0.75rem] text-red-300"
            >
              {t("med_history.overview.sync_status.acceptance_failed")}
            </Text>
          ) : null}

          {/* Action buttons */}
          <View className="mt-6 flex-row justify-end gap-3">
            {cardState === "editing" && (
              <TouchableOpacity
                onPress={() => {
                  capturePress(pressLabel("Cancel"), "cancel_intent_edit");
                  handleCancelEdit();
                }}
                activeOpacity={0.7}
                className="flex-row items-center gap-2 rounded-lg border border-white/10 px-4 py-2.5"
              >
                <Text className="text-[0.75rem] font-medium text-slate-400">
                  Cancel
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              onPress={() => {
                capturePress(
                  pressLabel(isSaving ? "Saving..." : getContinueButtonText()),
                  "continue_intent",
                );
                handleContinue();
              }}
              disabled={!canContinue() || isSaving}
              activeOpacity={0.7}
              className={`flex-row items-center gap-2 rounded-lg px-5 py-2.5 shadow-[0_0_20px_rgba(255,255,255,0.1)] ${
                canContinue() && !isSaving ? "bg-white" : "bg-white/50"
              }`}
            >
              <Text
                className={`text-[0.75rem] font-semibold tracking-wide ${
                  canContinue() && !isSaving
                    ? "text-[#020617]"
                    : "text-slate-400"
                }`}
              >
                {isSaving ? "Saving..." : getContinueButtonText()}
              </Text>
              {canContinue() && !isSaving && (
                <ArrowRight size={14} color="#020617" />
              )}
            </TouchableOpacity>
          </View>

          {/* Medical History Link - only show on first step */}
          {/* Access full medical history link - visible in all steps */}
          <View className="mt-5 border-t border-white/5 pt-3">
            <TouchableOpacity
              onPress={() => {
                capturePress(
                  pressLabel("Access full medical history"),
                  "open_medical_history",
                );
                handleAccessMedicalHistory();
              }}
              activeOpacity={0.7}
              className="flex-row items-center justify-between rounded-lg px-2 py-2"
            >
              <View className="flex-row items-center gap-2.5">
                <History size={14} color="#64748b" />
                <Text className="text-[0.7rem] font-medium text-slate-500">
                  Access full medical history
                </Text>
              </View>
              <ChevronRight size={14} color="#64748b" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Bottom Sheet for "Other" reason - Simple animated view */}
        {showOtherSheet && (
          <>
            {/* Backdrop */}
            <TouchableOpacity
              activeOpacity={1}
              onPress={closeOtherSheet}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 9998,
              }}
            />

            {/* Sheet */}
            <Animated.View
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                backgroundColor: "#0F172A",
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                borderTopWidth: 1,
                borderTopColor: "rgba(255, 255, 255, 0.1)",
                padding: 24,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: -4 },
                shadowOpacity: 0.3,
                shadowRadius: 8,
                elevation: 10,
                transform: [
                  {
                    translateY: slideAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [400, 0], // Start off-screen, slide up
                    }),
                  },
                ],
              }}
            >
              {/* Header */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: 20,
                }}
              >
                <Text
                  style={{
                    fontFamily: "Tobias-Thin",
                    fontSize: 18,
                    fontWeight: "500",
                    lineHeight: 24,
                    color: "#ffffff",
                    flex: 1,
                    paddingRight: 32,
                  }}
                >
                  You&apos;ve selected other, please provide us any additional
                  info about it
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    // X-icon with no visible copy to name it.
                    capturePress("close_other_sheet");
                    closeOtherSheet();
                  }}
                  style={{
                    borderRadius: 20,
                    padding: 4,
                  }}
                >
                  <X size={20} color="#94a3b8" />
                </TouchableOpacity>
              </View>

              {/* Text Input */}
              <View
                style={{
                  position: "relative",
                  width: "100%",
                  marginBottom: 20,
                }}
              >
                <View
                  style={{
                    position: "absolute",
                    left: 12,
                    top: 14,
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  <Pencil size={18} color="#94a3b8" />
                </View>
                <TextInput
                  placeholder="Feel free to write anything"
                  placeholderTextColor="#64748b"
                  value={otherReasonText}
                  onChangeText={setOtherReasonText}
                  multiline
                  numberOfLines={4}
                  style={{
                    minHeight: 100,
                    width: "100%",
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: "rgba(255, 255, 255, 0.1)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    padding: 12,
                    paddingLeft: 40,
                    fontSize: 14,
                    color: "#ffffff",
                    textAlignVertical: "top",
                  }}
                />
              </View>

              {/* Save Button */}
              <TouchableOpacity
                onPress={() => {
                  capturePress(pressLabel("Save info"), "save_other_reason");
                  saveOtherReason();
                }}
                activeOpacity={0.7}
                style={{
                  width: "100%",
                  borderRadius: 12,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  paddingVertical: 12,
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: "500",
                    color: "#ffffff",
                  }}
                >
                  Save info
                </Text>
              </TouchableOpacity>
            </Animated.View>
          </>
        )}
      </AnimatedRN.View>
    </>
  );
}
